import Parse from 'parse';

const DocumentPoolModule = {
  namespaced: true,
  state: {
    folder: [],
    files: [],
    tags: [],
    search: {
      files: [],
      folder: [],
    },
  },
  mutations: {
    setFolder(state, folder) {
      state.folder = folder;
    },
    setSearchResult(state, result) {
      state.search = result;
    },
    deleteFile(state, fileToDelete) {
      const ind = state.folder.findIndex((el) => el.id === fileToDelete.id);
      state.folder.splice(ind, 1);
    },
    deleteFolder(state, folderToDelete) {
      const ind = state.folder.findIndex((el) => el.id === folderToDelete.id);
      state.folder.splice(ind, 1);
    },
    editFolder(state, edited) {
      state.folder = state.folder.map((fol) => (fol.id === edited.id ? edited : fol));
    },
    setFiles(state, files) {
      state.files = files;
    },
    setTags(state, tags) {
      state.tags = tags;
    },
    addTag(state, tag) {
      state.tags.push(tag);
    },
    removeTag(state, deleted) {
      state.tags = state.tags.filter((tag) => tag.id !== deleted.id);
    },
  },
  actions: {
    async addFilesToFolder({ dispatch }, { files, currentFolder, tags }) {
      try {
        for (const currentFile of files) {
          const fileExtension = currentFile.type.split('/')[1];
          const type = ['txt', 'pdf'].includes(fileExtension) ? fileExtension : 'image';
          const fileName = currentFile.name.split('.')[0].replace(/\W/gi, '') + '.' + fileExtension;

          const parseFile = await new Parse.File(fileName, currentFile);
          const parseObjectFile = new Parse.Object('File');
          parseObjectFile.set('file', parseFile);
          parseObjectFile.set('name', currentFile.name);
          parseObjectFile.set('type', type);
          parseObjectFile.set('tags', tags);
          parseObjectFile.set('parentFolder', currentFolder);

          await parseObjectFile.save();
          await dispatch('document-pool/loadFolder', currentFolder, { root: true });
        }
      } catch (e) {
        console.log(e);
      }
    },
    async addFolder({ dispatch }, { name, currentFolder, auth, tags }) {
      let folder = new Parse.Object('Folder');
      folder.set('name', name);
      folder.set('auth', auth);
      folder.set('tags', tags);
      if (!currentFolder) {
        folder.set('level', 1);
      } else folder.set('parentFolder', currentFolder);
      try {
        folder = await folder.save();
        dispatch('loadFolder', currentFolder);
      } catch (e) {
        console.log(e);
      }
    },
    async addTag({ commit }, { name }) {
      const tag = await new Parse.Object('Tag').set('name', name).save();
      commit('addTag', tag);
    },
    async deleteFolder({ commit }, fol) {
      try {
        await Parse.Object.destroyAll(fol);
        commit('deleteFolder', fol);
      } catch (e) {
        console.log(e);
      }
    },
    // eslint-disable-next-line no-empty-pattern
    async deleteFile({ commit }, file) {
      try {
        await Parse.Object.destroyAll(file);
        commit('deleteFile', file);
      } catch (e) {
        console.log(e);
      }
    },
    async deleteTag({ commit }, { tag, folders }) {
      for (const fol of folders) {
        fol.set(
          'tags',
          fol.get('tags').filter((t) => t !== tag.id)
        );
        await fol.save();
      }
      await tag.destroy();
      commit('removeTag', tag);
    },
    async editFolder({ commit, dispatch }, { id, name, auth, tags, oldPath, hasNestedFolders }) {
      let folder = new Parse.Object('Folder');
      folder.id = id;
      folder.set('name', name);
      folder.set('auth', auth);
      folder.set('tags', tags);
      try {
        folder = await folder.save();
        commit('editFolder', folder);
        if (hasNestedFolders) dispatch('adjustPaths', { oldPath: oldPath + '/', newPath: folder.get('path') + '/' });
      } catch (e) {
        console.log(e);
      }
    },
    async adjustPaths({ commit }, { oldPath, newPath }) {
      const query = new Parse.Query('Folder');
      query.startsWith('path', oldPath);
      try {
        const result = await query.find();
        for (const fol of result) {
          const currentPath = newPath + fol.get('path').slice(newPath.length);
          fol.set('path', currentPath);
          try {
            let folder = await fol.save();
            commit('editFolder', folder);
          } catch (e) {
            console.log(e);
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    async loadFolder({ commit }, currentFolder) {
      try {
        const currentFolderId = currentFolder ? currentFolder.id : null;
        const { files, folder } = await Parse.Cloud.run('loadFolderAndFilesForCurrentFolder', { currentFolderId }, {});
        commit('setFiles', files);
        commit('setFolder', folder);
      } catch (e) {
        console.log(e);
      }
    },
    async loadClickedFolder({ commit }, { selFol }) {
      try {
        const query = new Parse.Query('Folder');
        query.equalTo('parentFolder', selFol);
        const folder = await query.find();
        commit('setFolder', folder);
      } catch (e) {
        console.log(e);
      }
    },
    async loadTags({ commit }) {
      try {
        const tagQuery = await new Parse.Query('Tag').find();
        commit('setTags', tagQuery);
      } catch (e) {
        console.log(e);
      }
    },
    async searchFolder({ commit }, search) {
      try {
        const result = await Parse.Cloud.run(
          'searchFolderAndFiles',
          { search: search.toLowerCase().replace(/\s+/g, '') },
          {}
        );
        commit('setSearchResult', result);
      } catch (e) {
        console.log(e);
      }
    },
    // eslint-disable-next-line no-empty-pattern
    async getAllFoldersWithTag({}, tag) {
      try {
        const query = new Parse.Query('Folder');
        query.equalTo('tags', tag);
        return await query.find();
      } catch (e) {
        console.log(e);
      }
    },
    // eslint-disable-next-line no-empty-pattern
    async getAllNestedFolders({}, path) {
      const query = new Parse.Query('Folder');
      query.startsWith('path', path);
      try {
        return await query.find();
      } catch (e) {
        console.log(e);
      }
    },
    // eslint-disable-next-line no-empty-pattern
    async getAllNestedFiles({}, folder) {
      const query = new Parse.Query('File');
      query.equalTo('parentFolder', folder);
      try {
        return await query.find();
      } catch (e) {
        console.log(e);
      }
    },
  },
  getters: {
    folder: (state) => state.folder,
    files: (state) => state.files,
    tags: (state) => state.tags,
    searchFolder: (state) => state.search.folder,
    searchFiles: (state) => state.search.files,
  },
};
export default DocumentPoolModule;
